import {
  ColumnDef,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { Keyword, Keywords, Regions, Site } from "~/api/Api";
import { Authorizations } from "~/api/types";
import { Icon } from "~/assets/icons/Icon";
import { canCollectionMember } from "~/helpers/authorizations";
import { formatNumber, shortenUrl } from "~/helpers/helpers";

import Button from "../ui/button/Button";
import DataTable, { DataTableColumnMeta } from "../ui/dataTable/DataTable";
import ProgressBar from "../ui/progressBar/ProgressBar";
import Actions from "./Actions";
import styles from "./KeywordTable.module.scss";
import KeywordTableActions from "./KeywordTableActions";

export type KeywordColumnSort = {
  id: keyof Keyword;
  desc: boolean;
};

// react-country-flag fetches from lipis/flag-icons which uses ISO 3166-1
// alpha-2 codes. A few of our region TLDs don't match ISO and need remapping.
const TLD_TO_ISO_COUNTRY: Record<string, string> = {
  uk: "gb",
  com: "us",
};

const tldToIsoCountry = (tld: string): string => TLD_TO_ISO_COUNTRY[tld] ?? tld;

type KeywordTableProps = {
  keywords: Keywords;
  isLoading: boolean;
  isRefetching?: boolean;
  regions: Regions;
  authorizations: Authorizations;
  openModal: () => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  selectedItems?: Keyword[];
  selectedItemsDelete?: Keyword[];
  selectedItemsRefresh?: Keyword[];
  setSelectedItemsDelete?: (selectedItems: Keyword[]) => void;
  setSelectedItemsRefresh?: (selectedItems: Keyword[]) => void;
  setRowSelection?: (rowSelection: Record<string, boolean>) => void;
  rowSelection?: Record<string, boolean>;
  onSelectionChange?: (selectedItems: Keyword[]) => void;
  refetch: () => void;
  site: Site | null | undefined;
  sorting: KeywordColumnSort[];
  onSortingChange: (sorting: KeywordColumnSort[]) => void;
};

const hasDestroyPermission = (
  authorizations: Authorizations,
  rowIndex: number,
) => canCollectionMember(authorizations, rowIndex, "destroy");

const getChangeRateWithIcon = (value: number) => (
  <>
    {value > 0 && (
      <div className={styles.percentUp}>
        {Math.ceil(value)}
        <Icon iconName="PercentUpIcon" />
      </div>
    )}
    {value < 0 && (
      <div className={styles.percentDown}>
        {Math.ceil(value)}
        <Icon iconName="PercentDownIcon" />
      </div>
    )}
  </>
);

export const KeywordTable = ({
  keywords,
  isLoading,
  isRefetching,
  regions,
  authorizations,
  onSelectionChange,
  openModal,
  searchTerm,
  setSearchTerm,
  refetch,
  site,
  sorting,
  onSortingChange,
}: KeywordTableProps) => {
  const [selectedItemsDelete, setSelectedItemsDelete] = useState<Keyword[]>([]);
  const [selectedItemsRefresh, setSelectedItemsRefresh] = useState<Keyword[]>(
    [],
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const columns = useMemo<ColumnDef<Keyword>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: ({ table }) => (
          <>
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              onClick={(e) => e.stopPropagation()}
              aria-label="Select all keywords"
              className={styles.selectAllCheckbox}
            />{" "}
            Keyword
          </>
        ),
        meta: {
          // Lets the URL sub-row break out of the cell on hover so the
          // user can read long ranking URLs without us reserving a wide
          // column for them.
          expandOnHover: true,
          // Two-line skeleton matching the live cell layout (checkbox +
          // keyword + URL row). Skeleton heights match the line-heights
          // of `--ui-text-14-regular` (14px / 20px) and
          // `--ui-text-12-regular` (12px / 16px) so the row height
          // doesn't shift when initial data lands.
          skeleton: () => (
            <div className={styles.flexContainer}>
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.checkboxContainer}>
                <Skeleton width={14} height={14} />
              </div>
              <div className={styles.nameBlock}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="40%" height={16} />
              </div>
            </div>
          ),
        } satisfies DataTableColumnMeta,
        cell: ({ row, cell }) => {
          const canDelete = hasDestroyPermission(authorizations, row.index);
          const rankingUrlRaw = row.original.ranking_url;
          const rankingUrl = rankingUrlRaw
            ? new URL(String(rankingUrlRaw))
            : null;

          return (
            <div className={styles.flexContainer}>
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  disabled={!canDelete}
                  title={!canDelete ? "No permissions to select this item" : ""}
                />
              </div>
              <div className={styles.nameBlock}>
                <Link to={`${row.original.id}`} className={styles.keyword}>
                  {cell?.getValue() as string}
                </Link>
                {rankingUrlRaw && (
                  <a
                    target="_blank"
                    href={String(rankingUrlRaw)}
                    className={styles.rankingUrl}
                    title={String(rankingUrlRaw)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon
                      iconName="LinkIcon"
                      width="0.85rem"
                      height="0.85rem"
                    />
                    <span className={styles.rankingUrlText}>
                      {rankingUrl ? shortenUrl(rankingUrl.pathname) : "N/A"}
                    </span>
                  </a>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "country",
        header: "Loc",
        meta: { align: "center" } satisfies DataTableColumnMeta,
        cell: (info) => {
          const row = info.row.original;
          const icon = regions[row.country]?.tld?.substring(1) || "us";
          const tooltipContent = row?.location_name
            ? `${row.country} — ${row.location_name}`
            : row.country;

          return (
            <span
              className={styles.countryFlag}
              data-tooltip-id="keyword-country-tooltip"
              data-tooltip-content={tooltipContent}
            >
              <ReactCountryFlag
                countryCode={tldToIsoCountry(icon)}
                svg
                style={{ width: "1.5em", height: "1.5em" }}
              />
            </span>
          );
        },
      },
      {
        accessorKey: "now_pos",
        header: "Rank",
        meta: { align: "right" } satisfies DataTableColumnMeta,
        cell: (info) => {
          const keyword = info.row.original;
          const nowPos = formatNumber(info.getValue() as number);
          const isShowTooltip =
            !keyword.pos_confirmed && keyword.now_pos && keyword.now_pos < 100;

          return (
            <span
              className={clsx({ [styles.rankWithTooltip]: isShowTooltip })}
              data-tooltip-id={
                isShowTooltip ? "keyword-rank-tooltip" : undefined
              }
            >
              <span>{nowPos}</span>
              {isShowTooltip && <span>*</span>}
            </span>
          );
        },
      },
      {
        accessorKey: "day_change",
        header: "Day",
        cell: (info) => {
          const value = info.getValue() as number | null;
          if (value === 0 || value === null) return "-";

          return getChangeRateWithIcon(value);
        },
      },
      {
        accessorKey: "week_change",
        header: "Week",
        cell: (info) => {
          const value = info.getValue() as number | null;
          if (value === 0 || value === null) return "-";

          return getChangeRateWithIcon(value);
        },
      },
      {
        accessorKey: "month_change",
        header: "Month",
        cell: (info) => {
          const value = info.getValue() as number | null;
          if (value === 0 || value === null) return "-";

          return getChangeRateWithIcon(value);
        },
      },
      {
        accessorKey: "life_change",
        header: "Life",
        cell: (info) => {
          const value = info.getValue() as number | null;
          if (value === 0 || value === null) return "-";

          return getChangeRateWithIcon(value);
        },
      },
      {
        accessorKey: "search_vol",
        header: "MSV",
        meta: { align: "right" } satisfies DataTableColumnMeta,
        cell: (info) => formatNumber(info.getValue() as number),
      },
      {
        accessorKey: "cmp",
        header: "CMP",
        cell: (info) => `${((info.getValue() as number) * 100).toFixed()}%`,
      },
      {
        accessorKey: "cpc",
        header: "CPC",
        cell: (info) => <div>${Number(info.getValue()).toFixed(2)}</div>,
      },
      {
        accessorKey: "alli_rating",
        header: "Score",
        cell: (info) => (
          <div style={{ width: "100px" }}>
            <ProgressBar
              min={info.getValue() as number}
              max={100}
              customText={`${Math.ceil(Number(info.getValue()))} %`}
            />
          </div>
        ),
      },
      {
        accessorKey: "refresh_interval",
        header: "Interval",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "button",
        header: "",
        cell: (info) => {
          const keyword = info.row.original;
          const rowIndex = info.row.index;

          return (
            <div className={styles.actions} key={`actions-${Date.now()}`}>
              <Actions
                site={site}
                refetch={refetch}
                keywordId={keyword.id}
                //@ts-expect-error fix once all table done
                keyword={keyword}
                rowIndex={rowIndex}
                //@ts-expect-error fix once all table done
                authorizations={authorizations}
              />
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [authorizations, regions, site, refetch],
  );

  useEffect(() => {
    if (!keywords?.length) {
      setSelectedItemsDelete([]);
      setSelectedItemsRefresh([]);
      onSelectionChange?.([]);

      return;
    }

    const selected = Object.keys(rowSelection)
      .filter((k) => rowSelection[k])
      .map((k) => ({ keyword: keywords[Number(k)], index: Number(k) }))
      .filter(({ keyword }) => Boolean(keyword));

    setSelectedItemsDelete(
      selected
        .filter(({ index }) => hasDestroyPermission(authorizations, index))
        .map(({ keyword }) => keyword),
    );
    setSelectedItemsRefresh(selected.map(({ keyword }) => keyword));
    onSelectionChange?.(selected.map(({ keyword }) => keyword));
  }, [rowSelection, keywords, authorizations]);

  const hasCreatePermission = authorizations?.collection?.includes("create");

  const selectedItems = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map((k) => keywords[Number(k)])
        .filter(Boolean) as Keyword[],
    [rowSelection, keywords],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={keywords || []}
        isLoading={isLoading}
        tableLayout="auto"
        onRefetch={refetch}
        isRefetching={isRefetching}
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: "Search",
        }}
        toolbarActions={
          <div className={styles.keywordTableHeaderLeft}>
            <KeywordTableActions
              refetch={refetch}
              selectedItems={selectedItems}
              selectedItemsDelete={selectedItemsDelete}
              setSelectedItemsDelete={setSelectedItemsDelete}
              selectedItemsRefresh={selectedItemsRefresh}
              setSelectedItemsRefresh={setSelectedItemsRefresh}
              authorizations={authorizations}
              setRowSelection={setRowSelection}
            />
            <Button onClick={openModal} disabled={!hasCreatePermission}>
              <span className={styles.keywordTableHeaderButtontext}>
                <Icon iconName="PlusIcon" /> Add Keyword
              </span>
            </Button>
          </div>
        }
        externalSorting={{
          state: sorting as SortingState,
          onChange: (state) => onSortingChange(state as KeywordColumnSort[]),
        }}
        rowSelection={{
          state: rowSelection,
          onChange: setRowSelection,
          enabled: (row) => hasDestroyPermission(authorizations, row.index),
        }}
        columnPinning={{ left: ["name"], right: ["button"] }}
      />
      {/*
        Tooltips rendered at the table level (rather than inside cells)
        for two reasons:
          1. They escape the cell's stacking context. The pinned name
             column has `z-index: 1` from sticky pinning and was sitting
             above tooltips that were rendered inside other cells.
          2. One <Tooltip> per type instead of one per row — content is
             driven by `data-tooltip-content` on the trigger element.
        Explicit `zIndex: 9999` because react-tooltip has no default
        z-index in its CSS.
      */}
      <Tooltip
        id="keyword-country-tooltip"
        place="top"
        style={{ borderRadius: "var(--border-radius)", zIndex: 9999 }}
      />
      <Tooltip
        id="keyword-rank-tooltip"
        place="top"
        style={{ borderRadius: "var(--border-radius)", zIndex: 9999 }}
        content="The position has changed recently, updating"
      />
    </>
  );
};
